import lodashGet from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import values from 'lodash/values.js';
import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
import ActivitySubmission from '@/models/ActivitySubmission.js';
import AttemptAuthorization from '@/models/AttemptAuthorization.js';
import Course from '@/models/Course.js';
import CourseTemplate from '@/models/CourseTemplate.js';
import { reportFileUploadServerError } from '@/utils/reportFileUploadRejection.js';

/**
 * useActivity — the single documented data/behaviour surface for the learner-facing
 * course activity-type renderers (SlideDeck, FileSubmission, AcknowledgeInformation,
 * ViewInformation, Podcast, Ebook, LtiTool, LtiResourceLink, VideoTemplate).
 *
 * This consolidates the three mixins these renderers historically combined —
 * CourseActivity.mixin.js + AttemptAuthorization.mixin.js + IsLate.mixin.js — into
 * one composable so a reader (human or AI) sees the whole contract in one file.
 * Behaviour is intentionally identical to the merged mixins (the renderers used the
 * order [IsLate, CourseActivity, AttemptAuthorization], so CourseActivity's `get` and
 * `completed` win over IsLate's — that resolution is baked in below).
 *
 * Globals ($store/$route/$gate/$can/$moment/$handleResponseError) are read lazily off
 * the component proxy at access time — exactly as the old mixins read `this.*`. They
 * are intentionally NOT cached at setup time: test-utils `global.mocks` and some
 * plugin globals are not yet present on the proxy during setup().
 *
 * Renderers with a reduced surface (Discussion = late + auth only; Quiz = late + auth
 * + group-permission + remediation) are NOT served by this aggregate yet; they get
 * their own subset composition when migrated.
 *
 * @param {object} props - the component's reactive props (namespace, uuid, readOnly, provideSubmissions)
 * @param {{ emit: Function }} ctx - the setup context (for the `fetch-submissions` event)
 * @param {{ getPayload?: () => object, uploadContext?: string }} [options] - lazy payload builder used by submit(),
 *   and the FILE_UPLOAD_CONTEXT recorded when a submit carrying new media fails (no context, no record)
 */
export function useActivity(props, ctx, options = {}) {
    const instance = getCurrentInstance();

    if (!instance) {
        throw new Error('useActivity() must be called inside setup().');
    }

    const { proxy } = instance;
    const { emit } = ctx;
    const getPayload = options.getPayload ?? (() => ({}));

    // --- identity / auth user -------------------------------------------------
    const authUser = computed(() => proxy.$store.state.Auth.user);

    const routeActivityId = computed(() => Number(proxy.$route.params.activityId));

    // --- core activity data (was CourseActivity.mixin) ------------------------
    const get = field => proxy.$store.getters[`${props.namespace}/${props.uuid}/get`](field);

    const activity = computed(() => {
        if (props.readOnly) return proxy.$store.getters[`${props.namespace}/get`](props.uuid);

        return proxy.$store.getters[`${props.namespace}/getByKey`]({ keyName: 'id', key: routeActivityId.value });
    });

    // Prefer the loaded activity's own id (populated on read-only routes that load by
    // uuid and may not have an activityId route param); fall back to the route param.
    const activityId = computed(() => activity.value?.id ?? routeActivityId.value);

    const isCourseTemplate = computed(() => proxy.$route.name.includes('template'));

    // Policies load their own relations, so the course needs none here (P6).
    const course = computed(() =>
        isCourseTemplate.value
            ? CourseTemplate.query().find(proxy.$route.params.id)
            : Course.query().find(proxy.$route.params.courseId)
    );

    const activityContent = computed(() => get('activity_content'));

    const media = computed(() => get('media'));

    const settings = computed(() =>
        props.namespace.toLowerCase().includes('temp') ? 'activity_settings_template' : 'activity_settings'
    );

    const maxAttempts = computed(() => activity.value.activity_settings.max_attempts_allowed);

    const allowsMultipleSubmissions = computed(() => maxAttempts.value > 1);

    const latestUserSubmission = computed(() =>
        proxy.$store.getters['ActivitySubmissions/latestUserSubmission'](activityId.value)
    );

    const userSubmissions = computed(() => {
        const submissions = !isEmpty(props.provideSubmissions)
            ? props.provideSubmissions
            : values(proxy.$store.getters['ActivitySubmissions/all']());

        return submissions.filter(
            submission =>
                lodashGet(submission, 'created_by') === proxy.$store.getters['Auth/userId'] &&
                lodashGet(submission, 'activity_id') === activityId.value
        );
    });

    const allSubmissions = computed(() => {
        const submissions = !isEmpty(props.provideSubmissions)
            ? props.provideSubmissions
            : values(proxy.$store.getters['ActivitySubmissions/all']());

        return submissions.filter(submission => lodashGet(submission, 'activity_id') === activityId.value);
    });

    const submissions = computed(() =>
        proxy.$gate.allows('viewOther', ['ActivitySubmission', course.value])
            ? allSubmissions.value
            : userSubmissions.value
    );

    // Completion is per-user: key off the logged-in user's OWN submissions, not the (possibly
    // all-users) `submissions` list a viewOther instructor/admin sees. Otherwise another learner's
    // submission would hide the instructor's own Mark Complete and force the acknowledge checkbox on.
    const completed = computed(() => userSubmissions.value.length > 0 || lodashGet(activity.value, 'completed'));

    const userGrades = computed(() =>
        values(proxy.$store.getters['GradebookGrades/all']?.() ?? []).filter(
            grade => grade.activity_id === activityId.value && grade.user_id === proxy.$store.getters['Auth/userId']
        )
    );

    const enrollment = computed(() => proxy.$store.state.ActiveEnrollment.enrollment);
    const enrollmentPermissions = computed(() => proxy.$store.getters['ActiveEnrollment/permissions']);

    const canViewSubmissions = computed(
        () =>
            proxy.$gate.allows('viewAny', ['ActivitySubmission', course.value]) ||
            proxy.$gate.allows('viewOther', ['ActivitySubmission', course.value])
    );

    const canSubmitActivity = computed(() => proxy.$gate.allows('create', ['ActivitySubmission', course.value]));

    const instructor = computed(() => {
        const roles = proxy.$store.getters['ActiveEnrollment/roles'];

        if (!roles) return false;

        return roles.some(role => role.name === 'Instructor');
    });

    const admin = computed(() => proxy.$store.getters['User/hasRole']('Admin'));

    const hasPermissionToSubmit = computed(() => proxy.$can('create', 'activity submission') && !instructor.value);

    // --- late (was IsLate.mixin) ----------------------------------------------
    // `now` starts null and is populated on mount (the mixin did this in created()),
    // then ticks every second. onUnmounted clears the interval — fixing the leak the
    // mixin had.
    const now = ref(null);

    const isLate = computed(() => {
        if (!now.value) return false;

        return proxy.$store.getters[`${props.namespace}/${props.uuid}/isLate`](now.value);
    });

    const canSubmitLate = computed(() => {
        if (has(activity.value, 'activity_settings.allow_submissions_after_due_date')) {
            return lodashGet(activity.value, 'activity_settings.allow_submissions_after_due_date');
        }

        return get(settings.value).allow_submissions_after_due_date;
    });

    // --- attempt authorizations (was AttemptAuthorization.mixin) ---------------
    const anyAttemptAuthorizations = computed(() => {
        const id = activity.value?.id || activityId.value;

        const query = AttemptAuthorization.query().where('activity_id', id);

        if (!course.value || proxy.$gate.denies('viewAny', ['AttemptAuthorization', course.value])) {
            query.where('user_id', authUser.value.id);
        }

        return query.get();
    });

    const usedAttemptAuthorizations = computed(() =>
        anyAttemptAuthorizations.value.filter(attemptAuth => attemptAuth.used_at !== null)
    );

    const attemptAuthorizations = computed(() =>
        anyAttemptAuthorizations.value.filter(attemptAuth => attemptAuth.used_at === null)
    );

    const userAuthorizedAttempts = computed(() =>
        anyAttemptAuthorizations.value.filter(a => a.user_id === authUser.value.id)
    );

    const userUnusedAuthorizedAttempts = computed(() =>
        anyAttemptAuthorizations.value
            .filter(a => a.user_id === authUser.value.id && a.used_at === null)
            .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))
    );

    const userHasAuthorizedAttempt = computed(() => userUnusedAuthorizedAttempts.value.length > 0);
    const canUseAttemptAuthorization = computed(() => userUnusedAuthorizedAttempts.value.length > 0);
    const hasAnyAttemptAuthorizations = computed(() => userAuthorizedAttempts.value.length > 0);

    const hasExistingGrade = computed(() => userGrades.value.length > 0);

    const submitBlockedByExistingGrade = computed(() => hasExistingGrade.value && !userHasAuthorizedAttempt.value);

    // Show Submit when: unused authorized re-attempt (even if already completed),
    // blocked-by-grade (disabled button + tooltip), or not yet completed.
    // Do not early-return on completed alone — that made userHasAuthorizedAttempt dead.
    const canShowSubmit = computed(() => {
        if (props.readOnly) {
            return false;
        }

        return userHasAuthorizedAttempt.value || submitBlockedByExistingGrade.value || !completed.value;
    });

    const usedAllAttemptAuthorizations = computed(
        () =>
            anyAttemptAuthorizations.value.length !== 0 &&
            anyAttemptAuthorizations.value.length === usedAttemptAuthorizations.value.length
    );

    const submissionCount = computed(() => submissions.value?.length || 0);

    const traditionalAttemptsRemaining = computed(() => {
        if (isLate.value && !activity.value.activity_settings.allow_submissions_after_due_date) {
            return 0;
        }

        return Math.max(0, activity.value.activity_settings.max_attempts_allowed - submissionCount.value);
    });

    const authorizedAttemptsRemaining = computed(
        () => attemptAuthorizations.value.filter(a => a.user_id === authUser.value.id).length
    );

    const attemptsRemaining = computed(() => traditionalAttemptsRemaining.value + authorizedAttemptsRemaining.value);

    // --- derived gate (CourseActivity.disabled, references late + auth) --------
    const disabled = computed(() => {
        if (submitBlockedByExistingGrade.value) return true;
        if (usedAllAttemptAuthorizations.value) return true;
        if (userHasAuthorizedAttempt.value) return false;

        return (isLate.value && !canSubmitLate.value) || !hasPermissionToSubmit.value;
    });

    // --- methods --------------------------------------------------------------
    const isLoading = ref(false);
    const submissionsLoading = ref(true);

    const verifyActivity = () =>
        proxy.$store.dispatch('Activities/verifyActivity', {
            course_id: proxy.$route.params.courseId,
            activity_id: proxy.$route.params.activityId,
        });

    const fetchSubmissions = async () => {
        const filters = { activityId: activityId.value };

        if (!proxy.$gate.allows('viewOther', ['ActivitySubmission', course.value])) {
            filters.createdBy = proxy.$store.getters['Auth/userId'];
        }

        try {
            if (!['survey', 'quiz'].includes(activity.value?.activity_type)) {
                const data = await proxy.$store.dispatch('ActivitySubmissions/fetch', filters);

                await ActivitySubmission.insert({ data });
            }

            emit('fetch-submissions', () => {
                submissionsLoading.value = false;
            });
        } catch (error) {
            proxy.$handleResponseError(error);
            submissionsLoading.value = false;
        }
    };

    const fetchAuthUserSubmissions = async () => {
        const filters = { activityId: activityId.value, createdBy: proxy.$store.getters['Auth/userId'] };

        try {
            if (!['survey', 'quiz'].includes(activity.value?.activity_type)) {
                const data = await proxy.$store.dispatch('ActivitySubmissions/fetch', filters);

                await ActivitySubmission.insert({ data });
            }

            emit('fetch-submissions', () => {
                submissionsLoading.value = false;
            });
        } catch (error) {
            proxy.$handleResponseError(error);
            submissionsLoading.value = false;
        }
    };

    const submit = async () => {
        isLoading.value = true;
        let newMedia = [];

        try {
            const payload = getPayload();
            const sentMedia = payload?.activity_submission?.media ?? payload?.media;

            // Read the new files before the request, since the selection can change while it runs.
            newMedia = Array.isArray(sentMedia) ? sentMedia.filter(item => item && !item.id) : [];

            await proxy.$store.dispatch(`${props.namespace}/completeActivity`, payload);

            proxy.$store.commit(`Activities/${props.uuid}/EDIT_FIELD`, {
                field: 'completed',
                value: proxy.$moment.utc().format('YYYY-MM-DD HH:mm:ss'),
            });
        } catch (error) {
            if (options.uploadContext && newMedia.length > 0) {
                reportFileUploadServerError(options.uploadContext, newMedia, error);
            }

            proxy.$handleResponseError(error);
        } finally {
            isLoading.value = false;
        }
    };

    const toggleSubmissionLoading = finishLoading => finishLoading();

    // Mirrors the mixins' created() hooks: AttemptAuthorization required an auth user,
    // IsLate seeded `now` + a ticking interval, and CourseActivity pulled the auth
    // user's own submissions.
    let intervalId;

    onMounted(() => {
        if (!authUser.value) {
            throw new Error('Auth user must be defined');
        }

        now.value = proxy.$moment.utc();
        intervalId = setInterval(() => (now.value = proxy.$moment.utc()), 1000);

        if (!props.readOnly && isEmpty(submissions.value)) {
            fetchAuthUserSubmissions();
        }
    });

    onUnmounted(() => clearInterval(intervalId)); // fixes the historical setInterval leak

    return {
        // identity / auth
        authUser,
        activityId,
        // data
        activity,
        isCourseTemplate,
        course,
        activityContent,
        media,
        settings,
        maxAttempts,
        allowsMultipleSubmissions,
        latestUserSubmission,
        submissions,
        userSubmissions,
        allSubmissions,
        completed,
        userGrades,
        hasExistingGrade,
        submitBlockedByExistingGrade,
        canShowSubmit,
        enrollment,
        enrollmentPermissions,
        canViewSubmissions,
        canSubmitActivity,
        instructor,
        admin,
        hasPermissionToSubmit,
        get,
        // late
        now,
        isLate,
        canSubmitLate,
        // attempt authorizations
        anyAttemptAuthorizations,
        attemptAuthorizations,
        usedAttemptAuthorizations,
        userAuthorizedAttempts,
        userUnusedAuthorizedAttempts,
        userHasAuthorizedAttempt,
        canUseAttemptAuthorization,
        hasAnyAttemptAuthorizations,
        usedAllAttemptAuthorizations,
        submissionCount,
        traditionalAttemptsRemaining,
        authorizedAttemptsRemaining,
        attemptsRemaining,
        // derived
        disabled,
        // state + methods
        isLoading,
        submissionsLoading,
        verifyActivity,
        fetchSubmissions,
        fetchAuthUserSubmissions,
        submit,
        toggleSubmissionLoading,
    };
}
